/**
 * WaAudioPlayer — reproductor de audio estilo WhatsApp con forma de onda.
 * DeliveryIcon — ticks de estado de entrega (✓ enviado, ✓✓ entregado, ✓✓ azul leído).
 *
 * Extraído de WaMessageThread para mantener ese archivo por debajo de 1500 líneas.
 * No depende del estado del thread padre — son componentes completamente autocontenidos.
 */

import { useRef, useState } from 'react';
import { Icon, Spinner } from '@/lib/polaris';
import {
  AlertCircleIcon,
  ArrowDownIcon,
  MicrophoneIcon,
  PauseCircleIcon,
  PersonIcon,
  PinFilledIcon,
  PlayIcon,
  ReplayIcon,
  StarFilledIcon,
} from '@/lib/polaris-icons';

// ─── DeliveryIcon ───

export function DeliveryIcon({ status, light, onRetry }: { status: string; light?: boolean; onRetry?: () => void }) {
  const base = light ? '#ffffffdd' : undefined;
  if (status === 'read') return <span style={{ color: light ? '#7fd8f5' : '#53bdeb', fontSize: 14, letterSpacing: -3 }}>✓✓</span>;
  if (status === 'delivered') return <span style={{ color: base ?? '#8696a0', fontSize: 14, letterSpacing: -3 }}>✓✓</span>;
  if (status === 'sent') return <span style={{ color: base ?? '#8696a0', fontSize: 14 }}>✓</span>;
  if (status === 'failed') {
    if (onRetry) {
      return (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onRetry(); }}
          className="wa-retry-icon-btn"
          aria-label="No se envió. Click para reintentar."
          title="No se envió. Click para reintentar."
        >
          <Icon source={AlertCircleIcon} tone="critical" />
        </button>
      );
    }
    return <span style={{ color: 'var(--p-color-text-critical)', fontSize: 13 }}>✗</span>;
  }
  return <span style={{ color: base ?? '#8696a0', fontSize: 11 }}>⏳</span>;
}

// ─── Quick reactions constant ───

export const QUICK_REACTIONS = ['👍', '❤️', '😂', '😮', '😢', '🙏'];

// ─── Helpers ───

function generateWaveform(seed: string, bars: number): number[] {
  let h = 0;
  for (let i = 0; i < seed.length; i++) { h = ((h << 5) - h + seed.charCodeAt(i)) | 0; }
  const raw: number[] = [];
  for (let i = 0; i < bars; i++) {
    h = ((h << 5) - h + i * 7 + 13) | 0;
    raw.push(0.12 + (Math.abs(h % 100) / 100) * 0.88);
  }
  const result: number[] = [];
  for (let i = 0; i < bars; i++) {
    const prev = raw[Math.max(0, i - 1)];
    const curr = raw[i];
    const next = raw[Math.min(bars - 1, i + 1)];
    result.push(prev * 0.25 + curr * 0.5 + next * 0.25);
  }
  return result;
}

// ─── WaAudioPlayer ───

export function WaAudioPlayer({
  src,
  isOutbound,
  timestamp,
  deliveryStatus,
  isNote,
  avatarUrl,
  isStarred,
  isPinned,
  knownDuration,
  onRetry,
  // Loading states: cuando el audio aún no ha descargado, el componente
  // mantiene el MISMO layout (waveform + mic + tiempo) pero el botón de
  // play se reemplaza por download/spinner/retry. Esto evita que el usuario
  // vea un placeholder distinto al audio "real" cuando finalmente carga.
  loadingState = 'loaded',
  onDownloadRetry,
}: {
  src: string;
  isOutbound: boolean;
  timestamp: string;
  deliveryStatus: string;
  isNote: boolean;
  avatarUrl?: string | null;
  isStarred?: boolean;
  isPinned?: boolean;
  knownDuration?: number;
  onRetry?: () => void;
  loadingState?: 'loaded' | 'pending' | 'downloading' | 'failed';
  onDownloadRetry?: () => void;
}) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [playing, setPlaying] = useState(false);
  const [duration, setDuration] = useState(knownDuration ?? 0);
  const [currentTime, setCurrentTime] = useState(0);
  const [playbackRate, setPlaybackRate] = useState(1);
  // Waveform deterministic: usa src (o messageId fallback) como seed para
  // que se mantenga igual cuando cambia de 'pending' → 'loaded'.
  const waveformSeed = src && !src.startsWith('blob:') ? src : (knownDuration?.toString() ?? 'placeholder');
  const waveform = useRef(generateWaveform(waveformSeed, 52)).current;
  const isReady = loadingState === 'loaded';

  const progress = duration > 0 ? currentTime / duration : 0;

  const fmtTime = (s: number) => {
    if (!Number.isFinite(s) || s < 0) return '0:00';
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (playing) { audio.pause(); }
    else { audio.playbackRate = playbackRate; void audio.play(); }
    setPlaying(!playing);
  };

  const cycleSpeed = () => {
    const next = playbackRate === 1 ? 1.5 : playbackRate === 1.5 ? 2 : 1;
    setPlaybackRate(next);
    if (audioRef.current) audioRef.current.playbackRate = next;
  };

  const handleSeek = (e: React.MouseEvent<HTMLDivElement>) => {
    const audio = audioRef.current;
    if (!audio || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    audio.currentTime = pct * duration;
    setCurrentTime(pct * duration);
  };

  const dotColor = '#30b5e9';
  const playedColor = isOutbound ? '#5cb89a' : '#6b8e7b';
  const unplayedColor = isOutbound ? '#9ec5a1' : '#c5c5c5';

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, width: 310 }}>
      {/* Audio element solo si está cargado (sin src no se puede preload).
          Cuando loadingState !== 'loaded', el waveform es decorativo. */}
      {isReady && <audio
        ref={audioRef}
        src={src}
        preload="metadata"
        onLoadedMetadata={(e) => {
          const d = e.currentTarget.duration;
          if (Number.isFinite(d)) {
            setDuration(d);
          } else {
            // WebM from MediaRecorder may lack duration; resolve via seekable hack
            const audio = e.currentTarget;
            audio.currentTime = 1e10;
            audio.addEventListener('timeupdate', function fix() {
              if (Number.isFinite(audio.duration)) {
                setDuration(audio.duration);
              }
              audio.currentTime = 0;
              audio.removeEventListener('timeupdate', fix);
            }, { once: true });
          }
        }}
        onTimeUpdate={(e) => setCurrentTime(e.currentTarget.currentTime)}
        onEnded={() => { setPlaying(false); setCurrentTime(0); }}
      />}

      {/* Avatar — shows person+mic when idle, speed pill when playing or paused mid-playback */}
      <div style={{ flexShrink: 0, position: 'relative', width: 48, height: 48 }}>
        {(() => {
          const showSpeedPill = playing || currentTime > 0;
          return (
            <>
              <div
                style={{
                  width: 48, height: 48, borderRadius: '50%',
                  background: isOutbound ? '#c8e6c0' : '#dfe5e7',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  overflow: 'hidden',
                  cursor: showSpeedPill ? 'pointer' : 'default',
                }}
                onClick={showSpeedPill ? cycleSpeed : undefined}
                title={showSpeedPill ? `Velocidad: ${playbackRate}x — clic para cambiar` : undefined}
              >
                {showSpeedPill ? (
                  <div style={{
                    background: '#05a884',
                    borderRadius: 10,
                    padding: '2px 8px',
                    color: '#fff',
                    fontSize: 12,
                    fontWeight: 700,
                    lineHeight: '16px',
                    letterSpacing: -0.3,
                    userSelect: 'none',
                  }}>
                    {playbackRate === 1 ? '1' : playbackRate === 1.5 ? '1.5' : '2'}
                  </div>
                ) : avatarUrl ? (
                  <img src={avatarUrl} alt="" style={{ width: 48, height: 48, objectFit: 'cover' }} />
                ) : (
                  <span style={{ color: '#8696a0' }}><Icon source={PersonIcon} /></span>
                )}
              </div>
            </>
          );
        })()}
        {!(playing || currentTime > 0) && (
          <div style={{
            position: 'absolute', bottom: -1, right: -1,
            width: 18, height: 18, borderRadius: '50%',
            background: '#05a884',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            border: `2px solid ${isOutbound ? '#d9fdd3' : '#fff'}`,
          }}>
            <span style={{ color: '#fff', display: 'flex', transform: 'scale(0.45)' }}><Icon source={MicrophoneIcon} /></span>
          </div>
        )}
      </div>

      {/* Play + waveform + duration row */}
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
        {/* Top row: play/download/spinner/retry + waveform */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          {/* Botón izquierdo: cambia según loadingState pero MANTIENE
              el mismo tamaño y posición. Mismo layout que el audio
              ya descargado para que la transición sea seamless.
                - 'loaded':       ▶ play / ⏸ pause (toggle audio)
                - 'pending':      ↓ download (clic = trigger retry)
                - 'downloading':  ⟳ spinner (no clickeable)
                - 'failed':       ↻ retry (clic = trigger retry) */}
          {isReady ? (
            <button
              type="button"
              onClick={togglePlay}
              style={{
                width: 28, height: 28,
                border: 'none', background: 'transparent',
                cursor: 'pointer', padding: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <span style={{ color: '#5c6670' }}>
                <Icon source={playing ? PauseCircleIcon : PlayIcon} />
              </span>
            </button>
          ) : loadingState === 'downloading' ? (
            <div
              style={{
                width: 28, height: 28,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <Spinner size="small" />
            </div>
          ) : (
            <button
              type="button"
              onClick={onDownloadRetry}
              disabled={!onDownloadRetry}
              style={{
                width: 28, height: 28,
                border: 'none', background: 'transparent',
                cursor: onDownloadRetry ? 'pointer' : 'default', padding: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0,
              }}
              title={loadingState === 'failed' ? 'No se pudo descargar — clic para reintentar' : 'Descargar audio'}
            >
              <span style={{ color: loadingState === 'failed' ? '#e53935' : '#5c6670' }}>
                <Icon source={loadingState === 'failed' ? ReplayIcon : ArrowDownIcon} />
              </span>
            </button>
          )}

          {/* Waveform */}
          <div
            style={{ flex: 1, height: 26, cursor: 'pointer', display: 'flex', alignItems: 'center' }}
            onClick={handleSeek}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 1, position: 'relative', height: '100%' }}>
              {/* Progress dot */}
              <div style={{
                position: 'absolute',
                left: `calc(${progress * 100}% - 5px)`,
                top: '50%', transform: 'translateY(-50%)',
                width: 11, height: 11, borderRadius: '50%',
                background: dotColor, zIndex: 2,
                boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                transition: 'left 0.1s linear',
                pointerEvents: 'none',
              }} />
              {waveform.map((h, i) => {
                const barPct = i / waveform.length;
                return (
                  <div
                    key={i}
                    style={{
                      height: Math.max(5, Math.round(h * 26)),
                      borderRadius: 1,
                      background: barPct < progress ? playedColor : unplayedColor,
                      width: 3,
                      flexShrink: 0,
                    }}
                  />
                );
              })}
            </div>
          </div>
        </div>

        {/* Bottom row: duration left + timestamp right */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingLeft: 32 }}>
          <span style={{ fontSize: 11, color: '#667781', lineHeight: 1 }}>
            {fmtTime(playing || currentTime > 0 ? currentTime : duration)}
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
            {isStarred && (
              <span style={{ color: '#fbbf24', display: 'flex', flexShrink: 0, transform: 'scale(0.6)' }}><Icon source={StarFilledIcon} /></span>
            )}
            {isPinned && (
              <span style={{ color: '#8696a0', display: 'flex', flexShrink: 0, transform: 'scale(0.6)' }}><Icon source={PinFilledIcon} /></span>
            )}
            <span style={{ fontSize: 11, color: '#667781' }}>{timestamp}</span>
            {isOutbound && !isNote ? <DeliveryIcon status={deliveryStatus} onRetry={onRetry} /> : null}
          </div>
        </div>
      </div>
    </div>
  );
}
